import React from 'react';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { SEO } from '../components/SEO';

const headingMain =
  'text-[32px] font-bold leading-[1.15] tracking-[-1px] text-[#0064E0] md:text-[48px]';
const headingSection = 'mt-10 text-[22px] font-semibold leading-tight text-[#0064E0]';
const linkClass = 'font-semibold text-[#0064E0] hover:underline';

export function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy — logNsec"
        description="How logNsec collects, uses, and protects your data."
        canonical="/privacy"
      />
      <section className="bg-white px-5 py-20 md:px-8">
      <div className="mx-auto max-w-3xl">
        <ScrollAnimate>
          <h1 className={headingMain}>Privacy Policy</h1>
          <p className="mt-3 text-label-site text-[#65676B]">Last updated: July 2025</p>

          <p className="mt-8 text-body-site">
            This Privacy Policy describes how <strong className="font-semibold text-[#1C1E21]">logNsec</strong>{' '}
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses, stores, and shares information when
            you visit <strong className="font-semibold text-[#1C1E21]">lognsec.com</strong>, contact us, or use our
            product <strong className="font-semibold text-[#1C1E21]">ShieldSME</strong> at{' '}
            <a
              href="https://shieldsme.lognsec.com"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              shieldsme.lognsec.com
            </a>
            . By using our website or services, you agree to the practices described here. If you do not agree,
            please do not use our sites or services.
          </p>

          <h2 className={headingSection}>1. Introduction &amp; who we are</h2>
          <p className="mt-4 text-body-site">
            logNsec provides cybersecurity products and related services. We were founded in{' '}
            <strong className="font-semibold text-[#1C1E21]">2025</strong>. We operate remotely and deliver our
            offerings globally. We are committed to handling personal and business-related information
            responsibly and in line with this policy.
          </p>
          <p className="mt-4 text-body-site">
            For any privacy-related questions or requests, contact us at{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>
            .
          </p>

          <h2 className={headingSection}>2. What data we collect</h2>
          <p className="mt-4 text-body-site">
            The information we collect depends on how you interact with us.
          </p>
          <h3 className="mt-6 text-lg font-semibold text-[#1C1E21]">Website visitors (lognsec.com)</h3>
          <p className="mt-3 text-body-site">We may collect or receive:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Technical and usage data</strong>, such as IP
              address, browser type and version, device type, general location derived from IP, pages visited,
              referring/exit pages, and timestamps. This may be collected through server logs, essential
              infrastructure (for example, content delivery and security), and analytics tools we use to
              understand how our site is used.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Information you choose to provide</strong>, such as
              your name, email address, company name, country, and message when you use our contact form or
              otherwise reach out to us.
            </li>
          </ul>
          <h3 className="mt-6 text-lg font-semibold text-[#1C1E21]">ShieldSME users (shieldsme.lognsec.com)</h3>
          <p className="mt-3 text-body-site">When you create or use a ShieldSME account, we may process:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Account and profile data</strong>, such as email
              address, name, company name, and preferences you set in the product.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Billing and payment-related data</strong> processed
              by our payment provider, <strong className="font-semibold text-[#1C1E21]">Paystack</strong>. We do not
              store full payment card details on our own systems; Paystack handles card transactions according to
              its own terms and security standards.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Security and service data</strong>, including inputs
              you provide for monitoring (for example, email-related signals you connect for threat monitoring),
              outputs from security checks and integrations (such as aggregated or technical results from threat
              intelligence or breach-related lookups), and operational logs needed to run and secure the service.
            </li>
          </ul>
          <p className="mt-4 text-body-site">
            We do not intentionally collect sensitive categories of personal data beyond what you or your
            organization choose to submit in connection with the service. Please do not send us information you are
            not authorized to share.
          </p>

          <h2 className={headingSection}>3. How we use the data</h2>
          <p className="mt-4 text-body-site">We use the information above to:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Deliver and operate</strong> our website, ShieldSME,
              and any related support or managed services you request.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Provide security monitoring and product features</strong>
              , including alerts, reporting, and integrations you enable.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Communicate with you</strong> about your account,
              service updates, security notices, and responses to inquiries.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Improve and develop</strong> our products (for
              example, understanding usage patterns, fixing issues, and enhancing reliability and security).
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Comply with law</strong>, enforce our terms, and
              protect the rights, safety, and security of logNsec, our users, and others.
            </li>
          </ul>

          <h2 className={headingSection}>4. Data storage and security</h2>
          <p className="mt-4 text-body-site">
            We use industry-standard measures designed to protect information against unauthorized access, loss, or
            misuse. Data is transmitted over encrypted connections (HTTPS/TLS). Data at rest is protected using
            safeguards provided by our hosting and database providers.
          </p>
          <p className="mt-4 text-body-site">
            ShieldSME and related application data are stored using{' '}
            <strong className="font-semibold text-[#1C1E21]">Supabase</strong> (PostgreSQL) and associated
            infrastructure. Our web applications and static assets may be hosted on{' '}
            <strong className="font-semibold text-[#1C1E21]">Vercel</strong>. No method of storage or transmission is
            completely secure; we work to maintain appropriate technical and organizational measures for our stage
            and risk profile.
          </p>

          <h2 className={headingSection}>5. Third-party services</h2>
          <p className="mt-4 text-body-site">
            We rely on trusted service providers to operate logNsec and ShieldSME. They process data on our behalf
            according to their own privacy policies and our agreements with them. These may include:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Supabase</strong> — authentication, database, and
              related backend services.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Vercel</strong> — hosting and delivery of web
              applications.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Groq</strong> — AI-assisted features (for example,
              summaries or briefs), where enabled.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Twilio</strong> — messaging channels such as WhatsApp
              notifications, where you opt in or configure them.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Paystack</strong> — payment processing for
              subscriptions or purchases.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">AlienVault OTX</strong> — threat intelligence data
              used in security features.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Have I Been Pwned</strong> — breach-related checks
              where integrated into the product.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Cloudflare</strong> — DNS, performance, and security
              services for our domains and traffic.
            </li>
          </ul>
          <p className="mt-4 text-body-site">
            We may add or change subprocessors as our services evolve. Where required, we will update this policy or
            provide notice as appropriate.
          </p>

          <h2 className={headingSection}>6. Data retention</h2>
          <p className="mt-4 text-body-site">
            We retain information only as long as necessary for the purposes described in this policy, unless a
            longer period is required or permitted by law. For example:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Account and product data</strong> are kept for the
              life of your account and a reasonable period afterward for backups, dispute resolution, and legal
              obligations.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Marketing or general inquiry records</strong> may be
              retained until you ask us to delete them or we no longer need them for our legitimate business
              purposes.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Logs and security records</strong> may be kept for a
              limited period to detect abuse, secure systems, and meet compliance needs.
            </li>
          </ul>
          <p className="mt-4 text-body-site">
            Retention periods can vary by data type and legal requirements. You may request deletion as described
            below, subject to exceptions we must honor.
          </p>

          <h2 className={headingSection}>7. Your rights</h2>
          <p className="mt-4 text-body-site">
            Depending on where you live, you may have rights to access, correct, update, or delete your personal
            information, or to object to or restrict certain processing. To exercise these rights, or to ask questions
            about how we handle your data, email{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>
            . We will respond within a reasonable timeframe and may need to verify your identity before acting on
            certain requests.
          </p>
          <p className="mt-4 text-body-site">
            If you are not satisfied with our response, you may have the right to complain to a data protection
            authority in your jurisdiction.
          </p>

          <h2 className={headingSection}>8. Cookies</h2>
          <p className="mt-4 text-body-site">
            We use cookies and similar technologies in a <strong className="font-semibold text-[#1C1E21]">minimal</strong>{' '}
            way — for example, to keep the site functioning, remember preferences where needed, support security, or
            measure basic site usage. We do <strong className="font-semibold text-[#1C1E21]">not</strong> use
            advertising cookies or sell your personal information based on cookie data.
          </p>
          <p className="mt-4 text-body-site">
            You can control cookies through your browser settings. Blocking some cookies may affect how certain
            features work.
          </p>

          <h2 className={headingSection}>9. Changes to this policy</h2>
          <p className="mt-4 text-body-site">
            We may update this Privacy Policy from time to time to reflect changes in our practices, products, or
            legal requirements. When we make material changes, we will post the updated policy on this page and
            revise the &quot;Last updated&quot; date at the top. We encourage you to review this policy periodically.
            Continued use of our website or services after changes become effective constitutes acceptance of the
            updated policy, where permitted by law.
          </p>

          <h2 className={headingSection}>10. Contact us</h2>
          <p className="mt-4 text-body-site">
            If you have questions about this Privacy Policy or our data practices, contact logNsec at:
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Email:</strong>{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Website:</strong>{' '}
            <a href="https://lognsec.com" className={linkClass}>
              lognsec.com
            </a>
          </p>
          <p className="mt-4 text-body-site">
            We do not maintain a public mailing address at this time; correspondence by email is the preferred
            channel.
          </p>
        </ScrollAnimate>
      </div>
    </section>
    </>
  );
}
